import { DOCUMENT, Location } from '@angular/common';
import { inject, Injectable, Injector, signal, WritableSignal } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { PlatformService, WindowService } from 'ngx-unificator/services';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { EnvironmentService } from './environment.service';
import { GlobalEventsService } from './global-events.service';
import { filterFirst } from 'ngx-unificator/rxjs';
import { StaticContentService } from './static-content.service';
import { LanguageService } from './language/language.service';
import { HIDDEN_LANG, ROOT_LANG } from '../helpers/router';
import { REQUEST } from '../../../express.tokens';
import { HostRequestsInterceptor } from '../interceptors/host-requests.interceptor';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private _meta = inject(Meta);
  private _title = inject(Title);
  private _location = inject(Location);
  private _env = inject(EnvironmentService);
  private _globalEvents = inject(GlobalEventsService);
  private _window = inject(WindowService);
  private _document = inject<Document>(DOCUMENT);
  private _static = inject(StaticContentService);
  private _lang = inject(LanguageService);
  private _platform = inject(PlatformService);
  private _injector = inject(Injector);
  private _host = inject(HostRequestsInterceptor);

  private _mapPrefixMirrorsLocales: WritableSignal<Record<string, { hreflang: string; domain?: string }>> = signal({});

  private _hreflangMap$ = this._static.item({ slug: 'seo-hreflang' }).pipe(
    filterFirst(data => !!data),
    map(item => item[0]?.Grouplist),
    tap(data => this._mapPrefixMirrorsLocales.set(data)),
    shareReplay(1),
  );

  public originalLocationUrl = signal<string>('');

  public metaTitle: string;

  public currentPageUrl = signal<string>(undefined);
  public domain = signal<string>(undefined);

  /**
   * Set in head Title & description
   * @param tags
   */
  public setMetaTags(tags: any) {
    this.metaTitle = tags.MetaTitle + ' - SpinSamurai casino';
    this._title.setTitle(tags.MetaTitle || 'SpinSamurai');
    this._meta.updateTag({ name: 'description', content: tags.MetaDesc || '' });

    if ('NoIndex' in tags && tags.NoIndex) {
      this.addMetaRobotNoIndex();
    }
  }

  /**
   * Add new alternate/canonical links and remove old links
   */
  public updateGlobalHeadLink() {
    this._addLinkPreconnectImg();
    this._addLinkPreconnectSsImg();
    this._addLinkPreconnectApi();
    this._hreflangMap$.pipe(switchMap(() => this._globalEvents.routerNavigationEnd$)).subscribe(e => {
      this._removeOldLinks(['alternate', 'canonical', 'x-default']);
      this._updateHeadLinks();
      this._addLangTagForHTML();
      this._setSeoDomainData(e);
      this._addLocaleToNoindex(e.url, HIDDEN_LANG);
    });
  }

  private _buildUrl(locale: string, domain: string, cleanPath: string): string {
    const base = `https://www.spinsamurai${domain}.com`;
    return locale === ROOT_LANG || locale === ''
      ? cleanPath != '' || locale === ROOT_LANG || locale === '' ?
        `${base}/${cleanPath}` : `${base}`
      : cleanPath != '' || locale === ROOT_LANG || locale === '' ?
        `${base}/${locale}/${cleanPath}` : `${base}/${locale}`;
  }

  private _addAlternateLinks(cleanPath: string, locales: string[]) {
    const hreflangMap = this._mapPrefixMirrorsLocales() || {};

    locales
      .filter(locale => locale !== HIDDEN_LANG)
      .forEach(locale => {
        const { domain = '', hreflang } = hreflangMap[locale] ?? {};
        const link = this._document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('href', this._buildUrl(locale, domain, cleanPath));
        if (hreflang) {
          link.setAttribute('hreflang', hreflang);
        }
        this._document.head.appendChild(link);
      });

    const defaultLink = this._document.createElement('link');
    defaultLink.setAttribute('rel', 'alternate');
    defaultLink.setAttribute('href', cleanPath != '' ? `https://www.spinsamurai.com/${cleanPath}` : `https://www.spinsamurai.com/`);
    defaultLink.setAttribute('hreflang', 'x-default');
    this._document.head.appendChild(defaultLink);
  }

  private _addCanonicalLink(cleanPath: string) {
    const hreflangMap = this._mapPrefixMirrorsLocales() || {};
    const currentLocale = this._lang.currentRouteLanguage;
    const { domain = '' } = hreflangMap[currentLocale] ?? {};
    const canonicalLink = this._document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', this._buildUrl(currentLocale, domain, cleanPath));
    this._document.head.appendChild(canonicalLink);
  }

  private _addOriginalUrl() {
    this.originalLocationUrl.set(this._window.nativeWindow.location.origin);
  }

  private _updateHeadLinks() {
    const locales = this._env.dataEnv().languageList.map(item => item.short);
    const segments = this._location.path().split('/').filter(Boolean);
    const cleanPath = locales.includes(segments[0]) ? segments.slice(1).join('/') : segments.join('/');

    this._addAlternateLinks(cleanPath, locales);
    this._addCanonicalLink(cleanPath);
    this._addOriginalUrl();
  }

  /**
   * Remove old alternate\canonical links from DOM
   */
  private _removeOldLinks(typeOfLinks: string[]) {
    typeOfLinks.forEach(rel => {
      this._document.querySelectorAll(`link[rel="${rel}"]`).forEach(link => this._document.head.removeChild(link));
    });
  }

  /**
   * Add in head <meta name="robots" content="noindex" />
   */
  public addMetaRobotNoIndex() {
    this._meta.addTags([{ name: 'robots', content: 'noindex' }]);
  }

  private _addLocaleToNoindex(url: string, lang: string) {
    if (url.startsWith(`/${lang}`) && !url.startsWith(`/${lang}-`)) {
      this._meta.addTags([{ name: 'robots', content: 'noindex, nofollow' }]);
    }
  }

  /**
   * Sets the `lang` attribute of the HTML <html> element
   */
  private _addLangTagForHTML() {
    const defaultLocale = ROOT_LANG;
    const matches =
      this._location.path().match(/^\/([a-z]{2}(?:-[a-z]{2})?)\//) || this._location.path().match(/^\/([a-z]{2}(?:-[a-z]{2})?)/)
    this._document.documentElement.lang =
      matches && this._env.dataEnv()?.languageList.some(e => e.short === matches[1]) ? matches[1] : defaultLocale;
  }

  private _addLinkPreconnectImg() {
    const link: HTMLLinkElement = this._document.createElement('link');
    const mediaDomain = this._getAppHost()
      .replace('www.', 'media.')
      .replace('stage.', 'stage-media.');
    link.setAttribute('rel', 'preconnect');
    link.setAttribute('href', mediaDomain);
    this._document.head.appendChild(link);
  }

  private _addLinkPreconnectSsImg() {
    const link: HTMLLinkElement = this._document.createElement('link');
    link.setAttribute('rel', 'preconnect');
    link.setAttribute('href', 'https://cdn.softswiss.net');
    this._document.head.appendChild(link);
  }

  private _addLinkPreconnectApi() {
    const linkSs: HTMLLinkElement = this._document.createElement('link');
    const linkCms: HTMLLinkElement = this._document.createElement('link');
    const ssDomain = this._getAppHost()
      .replace('www.', '')
      .replace('stage.', '');

    const cmsDomain = this._getAppHost()
      .replace('www.', 'api.')
      .replace('stage.', 'stage-api.');

    linkSs.setAttribute('rel', 'preconnect');
    linkSs.setAttribute('href', ssDomain);
    this._document.head.appendChild(linkSs);
    linkCms.setAttribute('rel', 'preconnect');
    linkCms.setAttribute('href', cmsDomain);
    this._document.head.appendChild(linkCms);
  }

  public addLinkPreloadImg(imgPath: string) {
    if (!this._platform.isBrowser) {
      const link: HTMLLinkElement = this._document.createElement('link');
      link.setAttribute('rel', 'preload');
      link.setAttribute('fetchPriority', 'high');
      link.setAttribute('as', 'image');
      link.setAttribute('href', imgPath);
      this._document.head.appendChild(link);
    }
  }

  private _getAppHost(): string | undefined {
    const host = this._platform.isBrowser ? window.location.host : this._injector.get(REQUEST).headers.host;
    return `https://${host}`;
  }

  private _setSeoDomainData(data) {
    this.domain.set(this._host.getDomainName()?.includes('localhost') ? this._host.getDomainName() : `https://${this._host.getDomainName()}`);
    const splitUrl = data?.url?.split('/');
    this.currentPageUrl.set(!this._lang.currentRouteLanguage ? splitUrl?.[1] : (splitUrl?.[2] || ''));
  }
}
